import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { PiUserPlus } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { useGetClientUsers } from 'services/clientUserManagment';
import { IUserResponse } from 'services/clientUserManagment/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getNodes } from 'utils/table/nodes.utils';

enum USERS_COLUMNS {
  nickname,
  firstName,
  lastName,
  email,
  phoneNumber,
}

const ClientUsers = () => {
  const { data, isLoading, fetchNextPage, isFetchingNextPage } =
    useGetClientUsers();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const columnsVisibility = useTableColumnsVisibility(
    'ClientUsers',
    USERS_COLUMNS
  );
  const tableData = getNodes<IUserResponse>(data?.pages.flat());

  const onSelectChange = (action: Action, state: State) => {
    navigate(`${state.id ?? state.ids[state.ids.length - 1]}/accounts`);
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });
  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`user.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <Table.CustomColumnsButton
                columns={USERS_COLUMNS}
                isClear
                visibility={columnsVisibility}
                i18nextPrefix=''
                customI18next='input.label'
              />
              <Button
                variant={BUTTON_VARIANT.submit}
                onClick={() => navigate('create', { replace: false })}>
                <Button.Text
                  text={t('button.create-what', { what: t('user.what') })}
                />
                <Button.Icon icon={<PiUserPlus />} />
              </Button>
            </>
          }
        />
      }>
      <Table
        id={columnsVisibility.tableId}
        select={select}
        data={tableData}
        loading={isLoading || isFetchingNextPage}
        onFullyScroll={fetchNextPage}
        displayMode
        columns={[
          {
            label: t('input.label.nickname'),
            renderCell: ({ userName }) => (
              <TableItem>
                <TableItem.Text text={userName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.nickname),
          },
          {
            label: t('input.label.firstName'),
            renderCell: ({ firstName }) => (
              <TableItem>
                <TableItem.Text text={firstName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.firstName),
          },
          {
            label: t('input.label.lastName'),
            renderCell: ({ lastName }) => (
              <TableItem>
                <TableItem.Text text={lastName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.lastName),
          },
          {
            label: t('input.label.email'),
            renderCell: ({ email }) => (
              <TableItem>
                <TableItem.Text text={email} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(USERS_COLUMNS.email),
          },
          {
            label: t('input.label.phoneNumber'),
            renderCell: ({ phoneNumber }) => (
              <TableItem>
                <TableItem.Text text={phoneNumber} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(USERS_COLUMNS.phoneNumber),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default ClientUsers;
